/**
 * AppRoutes
 * Central route registry for current production behavior.
 *
 * Current model:
 * - Public pages are mostly generated from `STATIC_PAGE_ROUTES` (legacy Django HTML snapshots).
 * - Firestore-backed public content pages are mounted for Articles/Gallery/Course Content.
 * - Admin/editor pages live under `/dashboard` and are role-gated.
 *
 * Note on "duplicate-looking" pages:
 * `HomePage` remains a retained dynamic homepage variant; the current homepage
 * still comes from static page content.
 */
import { Navigate, Route, Routes } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';
import DashboardLayout from '../components/layout/DashboardLayout';
import PublicLayout from '../components/layout/PublicLayout';
import { STATIC_PAGE_ROUTES } from '../content/staticPages';
import AboutPage from '../pages/AboutPage';
import CollectionPage from '../pages/CollectionPage';
import ContentDetailPage from '../pages/ContentDetailPage';
import LoginPage from '../pages/LoginPage';
import NotFoundPage from '../pages/NotFoundPage';
import StaticContentPage from '../pages/StaticContentPage';
import DashboardCollectionPage from '../pages/dashboard/DashboardCollectionPage';
import DashboardAuthorCreatorsPage from '../pages/dashboard/DashboardAuthorCreatorsPage';
import DashboardHomePage from '../pages/dashboard/DashboardHomePage';
import UploadsPage from '../pages/dashboard/UploadsPage';
import DashboardUsersPage from '../pages/dashboard/DashboardUsersPage';
import { USER_ROLES } from '../services/userService';

const homePage = STATIC_PAGE_ROUTES.find((page) => page.route === '/');
const otherPages = STATIC_PAGE_ROUTES.filter(
  (page) =>
    page.route !== '/' &&
    page.route !== '/about' &&
    page.route !== '/dw_contact'
);

export default function AppRoutes() {
  return (
    <Routes>
      {/* Public static-site shell: routes are generated from large content map. */}
      <Route element={<PublicLayout />}>
        {homePage ? (
          <Route
            index
            element={<StaticContentPage title={homePage.title} html={homePage.html} />}
          />
        ) : null}

        <Route path="about" element={<AboutPage />} />
        <Route path="dw_contact" element={<Navigate to="/" replace />} />
        <Route path="articles" element={<CollectionPage collectionKey="articles" />} />
        <Route
          path="articles/:id"
          element={<ContentDetailPage collectionKey="articles" />}
        />
        <Route path="gallery" element={<CollectionPage collectionKey="animations" />} />
        <Route
          path="gallery/:id"
          element={<ContentDetailPage collectionKey="animations" />}
        />
        <Route
          path="course-content"
          element={<CollectionPage collectionKey="models" />}
        />
        <Route
          path="course-content/:id"
          element={<ContentDetailPage collectionKey="models" />}
        />

        {/* Legacy aliases preserved while moving to cleaner public URLs. */}
        <Route path="animations" element={<CollectionPage collectionKey="animations" />} />
        <Route
          path="animations/:id"
          element={<ContentDetailPage collectionKey="animations" />}
        />
        <Route path="models" element={<CollectionPage collectionKey="models" />} />
        <Route
          path="models/:id"
          element={<ContentDetailPage collectionKey="models" />}
        />

        {otherPages.map((page) => (
          <Route
            key={page.route}
            path={page.route.slice(1)}
            element={<StaticContentPage title={page.title} html={page.html} />}
          />
        ))}

        <Route path="admin-login" element={<LoginPage />} />
      </Route>

      <Route
        element={
          <ProtectedRoute
            allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.EDITOR]}
            redirectTo="/admin-login"
            forbiddenTo="/admin-login"
          />
        }
      >
        {/* Dashboard shell: shared sidebar + nested tool pages. */}
        <Route path="/dashboard" element={<DashboardLayout />}>
          <Route index element={<DashboardHomePage />} />
          <Route
            path="articles"
            element={<DashboardCollectionPage collectionKey="articles" />}
          />
          <Route path="staff-profiles" element={<DashboardAuthorCreatorsPage />} />
          <Route
            path="authors"
            element={<Navigate to="/dashboard/staff-profiles" replace />}
          />
          <Route
            path="gallery"
            element={<DashboardCollectionPage collectionKey="animations" />}
          />
          <Route
            path="course-content"
            element={<DashboardCollectionPage collectionKey="models" />}
          />
          <Route
            path="animations"
            element={<Navigate to="/dashboard/gallery" replace />}
          />
          <Route
            path="models"
            element={<Navigate to="/dashboard/course-content" replace />}
          />
          <Route path="uploads" element={<UploadsPage />} />
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[USER_ROLES.ADMIN]}
                redirectTo="/dashboard"
                forbiddenTo="/dashboard"
              />
            }
          >
            {/* Staff role management is intentionally admin-only. */}
            <Route path="admin-users" element={<DashboardUsersPage />} />
            <Route
              path="staff-users"
              element={<Navigate to="/dashboard/admin-users" replace />}
            />
          </Route>
        </Route>
      </Route>

      <Route path="/home" element={<Navigate to="/" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
